/**
 * Global Cmd-K command palette.
 *
 * Federated search across flows, event-designs, mappings, value-mappings,
 * connections, runtimes, parameters, and nav items. Backed by
 * `GET /api/search` (server collects), ranked client-side via fuse.js for
 * fuzzy match. Recent searches persisted in localStorage.
 *
 * Bound to Cmd-K / Ctrl-K via the `useShortcuts` dispatcher in AppShell.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';

export interface SearchResult {
  kind:
    | 'flow'
    | 'event-design'
    | 'value-mapping'
    | 'message-mapping'
    | 'connection'
    | 'runtime'
    | 'parameter'
    | 'nav';
  id: string;
  title: string;
  subtitle?: string;
  url: string;
}

const RECENT_KEY = 'pipebase:cmdk:recent';
const RECENT_MAX = 8;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((s): s is string => typeof s === 'string').slice(0, RECENT_MAX);
    }
  } catch {
    /* ignore */
  }
  return [];
}

function saveRecent(list: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* ignore */
  }
}

const KIND_LABELS: Record<SearchResult['kind'], string> = {
  flow: 'Flows',
  'event-design': 'Event Designs',
  'value-mapping': 'Value Mappings',
  'message-mapping': 'Mappings',
  connection: 'Connections',
  runtime: 'Runtimes',
  parameter: 'Parameters',
  nav: 'Navigation',
};

const KIND_ORDER: SearchResult['kind'][] = [
  'nav',
  'flow',
  'event-design',
  'message-mapping',
  'value-mapping',
  'connection',
  'runtime',
  'parameter',
];

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recent, setRecent] = useState<string[]>(() => loadRecent());
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on open + focus.
  useEffect(() => {
    if (!open) return;
    setQ('');
    setActive(0);
    setRecent(loadRecent());
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  // Fetch results once on open. Server returns everything; we re-rank
  // locally with fuse.js as the user types.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    fetch('/api/search')
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((data) => {
        if (!alive) return;
        setResults(Array.isArray(data?.results) ? (data.results as SearchResult[]) : []);
      })
      .catch(() => {
        if (alive) setResults([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  // Fuse instance — rebuilt when results change.
  const fuse = useMemo(
    () =>
      new Fuse(results, {
        keys: [
          { name: 'title', weight: 0.7 },
          { name: 'subtitle', weight: 0.2 },
          { name: 'id', weight: 0.1 },
        ],
        threshold: 0.4,
        ignoreLocation: true,
      }),
    [results],
  );

  const filtered: SearchResult[] = useMemo(() => {
    const term = q.trim();
    if (!term) return results;
    return fuse.search(term).map((r) => r.item);
  }, [q, fuse, results]);

  const grouped = useMemo(() => {
    const g: Partial<Record<SearchResult['kind'], SearchResult[]>> = {};
    for (const r of filtered) {
      (g[r.kind] ??= []).push(r);
    }
    return g;
  }, [filtered]);

  const flatList: SearchResult[] = useMemo(() => {
    const out: SearchResult[] = [];
    for (const k of KIND_ORDER) {
      const items = grouped[k];
      if (items) out.push(...items);
    }
    return out;
  }, [grouped]);

  // Reset cursor when filter changes.
  useEffect(() => {
    setActive(0);
  }, [q]);

  const onPick = (r: SearchResult) => {
    if (q.trim()) {
      const next = [q.trim(), ...recent.filter((s) => s !== q.trim())].slice(0, RECENT_MAX);
      saveRecent(next);
      setRecent(next);
    }
    onClose();
    navigate(r.url);
  };

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(flatList.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        const sel = flatList[active];
        if (sel) {
          e.preventDefault();
          onPick(sel);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flatList, active]);

  if (!open) return null;

  let runningIdx = -1;
  return (
    <div
      className="cmdk-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div className="cmdk" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk__input-wrap">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            aria-hidden
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="M11 11l3.5 3.5" />
          </svg>
          <input
            ref={inputRef}
            className="cmdk__input"
            placeholder="Search flows, designs, mappings…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="cmdk-input"
            aria-label="Search"
          />
          <span className="cmdk__hint">esc</span>
        </div>

        <div className="cmdk__list" role="listbox" aria-label="Search results">
          {loading && (
            <div className="cmdk__empty">Loading…</div>
          )}

          {!loading && q.trim() === '' && recent.length > 0 && (
            <div>
              <div className="cmdk__group-label">Recent searches</div>
              {recent.map((term, i) => (
                <div
                  key={`recent-${i}`}
                  className="cmdk__item"
                  onClick={() => setQ(term)}
                  role="option"
                  aria-selected={false}
                >
                  <div className="cmdk__item-icon" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4">
                      <circle cx="7" cy="7" r="5.5" />
                      <path d="M7 4v3l2 1.5" />
                    </svg>
                  </div>
                  <div className="cmdk__item-text">
                    <div className="cmdk__item-name">{term}</div>
                    <div className="cmdk__item-sub">Recent search</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && flatList.length === 0 && q.trim() !== '' && (
            <div className="cmdk__empty">No results for “{q}”</div>
          )}

          {!loading && KIND_ORDER.map((k) => {
            const items = grouped[k];
            if (!items || items.length === 0) return null;
            return (
              <div key={k}>
                <div className="cmdk__group-label">{KIND_LABELS[k]}</div>
                {items.map((r) => {
                  runningIdx++;
                  const isActive = runningIdx === active;
                  const myIdx = runningIdx;
                  return (
                    <div
                      key={`${r.kind}-${r.id}`}
                      role="option"
                      aria-selected={isActive}
                      className={'cmdk__item' + (isActive ? ' is-active' : '')}
                      onMouseEnter={() => setActive(myIdx)}
                      onClick={() => onPick(r)}
                      data-testid="cmdk-result"
                    >
                      <div className={`cmdk__item-icon cmdk__item-icon--${r.kind}`} aria-hidden>
                        <KindIcon kind={r.kind} />
                      </div>
                      <div className="cmdk__item-text">
                        <div className="cmdk__item-name">{r.title}</div>
                        <div className="cmdk__item-sub">{r.subtitle ?? r.kind}</div>
                      </div>
                      <span className="cmdk__item-shortcut">↵</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KindIcon({ kind }: { kind: SearchResult['kind'] }) {
  switch (kind) {
    case 'flow':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'event-design':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="12" cy="12" r="3" />
          <circle cx="12" cy="12" r="9" strokeDasharray="2 3" />
        </svg>
      );
    case 'value-mapping':
    case 'message-mapping':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <path d="M3 6h7M14 6h7M3 18h7M14 18h7" />
          <path d="M10 6c0 4 4 8 4 12" />
        </svg>
      );
    case 'connection':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="12" r="3" />
          <path d="M9 12h6" />
        </svg>
      );
    case 'runtime':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
          <rect x="3" y="4" width="18" height="6" rx="1" />
          <rect x="3" y="14" width="18" height="6" rx="1" />
          <circle cx="6.5" cy="7" r="0.7" fill="currentColor" />
          <circle cx="6.5" cy="17" r="0.7" fill="currentColor" />
        </svg>
      );
    case 'parameter':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M5 7h14M5 12h14M5 17h8" />
        </svg>
      );
    case 'nav':
    default:
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <path d="M9 5l7 7-7 7" />
        </svg>
      );
  }
}
